import { Box, Checkbox, FormControlLabel, Grid } from "@mui/material";
import CustomDatePicker from "../../../../../components/CustomDatePicker";
import Flex from "../../../../../components/Flex";
import Input from "../../../../../components/Input";
import Text from "../../../../../components/Text";
import dayjs from "dayjs";
import IntakeBasicDetails from "./IntakeBasicDetails";
import { IntakeFormValues } from "./new-intake-form-contant/intake-constant";

interface IndividualPsychotherapyProps {
  values: IntakeFormValues;
  setFieldValue: (field: string, value: any) => void;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  touched: any;
  errors: any;
  viewOnly?: boolean;
}

const IndividualPsychotherapy = ({
  values,
  setFieldValue,
  handleChange,
  handleBlur,
  touched: _touched,
  errors: _errors,
  viewOnly,
}: IndividualPsychotherapyProps) => {
  return (
    <Flex gap={0} flexDirection={"column"}>
      {/* Patient Demographics Section */}
      <Text variant="subtitle1" fontWeight="500">
        Patient Demographics
      </Text>
      <IntakeBasicDetails
        values={values}
        setFieldValue={setFieldValue}
        handleChange={handleChange}
        handleBlur={handleBlur}
        touched={_touched}
        errors={_errors}
        viewOnly={viewOnly}
      />

      {/* Medical Information Section */}
      <Box>
        <Text variant="subtitle1" fontWeight="500">
          Medical Information
        </Text>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} sm={6} lg={4}>
            <Input
              label="Primary Diagnosis/ Condition"
              textFieldProps={{
                value: values?.individualPsychotherapy?.primaryDiagnosis || "",
                onChange: (e) =>
                  setFieldValue(
                    "individualPsychotherapy.primaryDiagnosis",
                    e.target.value
                  ),
                placeholder: "Selection Text",
                disabled: viewOnly,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={4}>
            <Input
              label="Secondary Diagnosis/ Condition"
              textFieldProps={{
                name: "individualPsychotherapy.secondaryDiagnosis",
                value:
                  values?.individualPsychotherapy?.secondaryDiagnosis || "",
                onChange: handleChange,
                onBlur: handleBlur,
                placeholder: "Selection Text",
                disabled: viewOnly,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={4}>
            <Input
              label="Primary Care Physician"
              textFieldProps={{
                value:
                  values?.individualPsychotherapy?.primaryCarePhysician || "",
                onChange: (e) =>
                  setFieldValue(
                    "individualPsychotherapy.primaryCarePhysician",
                    e.target.value
                  ),
                placeholder: "Selection Text",
                disabled: viewOnly,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Input
              label="Current Medications"
              textFieldProps={{
                name: "individualPsychotherapy.currentMedications",
                value:
                  values?.individualPsychotherapy?.currentMedications || "",
                onChange: handleChange,
                onBlur: handleBlur,
                placeholder: "Selection Text",
                disabled: viewOnly,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Input
              label="Allergies (Medications, Food, Environmental)"
              textFieldProps={{
                name: "individualPsychotherapy.allergies",
                value: values?.individualPsychotherapy?.allergies || "",
                onChange: handleChange,
                onBlur: handleBlur,
                placeholder: "Selection Text",
                disabled: viewOnly,
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Input
              label="Relevant Medical History"
              textFieldProps={{
                name: "individualPsychotherapy.relevantMedicalHistory",
                value:
                  values?.individualPsychotherapy?.relevantMedicalHistory || "",
                onChange: handleChange,
                onBlur: handleBlur,
                placeholder: "Selection Text",
                disabled: viewOnly,
              }}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Mental Health History Section - Commented out
      <Box sx={{ mt: 1 }}>
        <Text variant="subtitle1" fontWeight="500">
          Mental Health History
        </Text>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12}>
            <CustomTextArea
              disabled={viewOnly}
              label="Previous Mental Health Treatment"
              rows={2}
              variant="outlined"
              name="individualPsychotherapy.previousMentalHealthTreatment"
              placeholder="Previous therapy, psychiatry, hospitalizations, outcomes"
              onBlur={handleBlur}
              value={
                values?.individualPsychotherapy
                  ?.previousMentalHealthTreatment || ""
              }
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <CustomTextArea
              disabled={viewOnly}
              label="Psychiatric Medications"
              rows={2}
              variant="outlined"
              name="individualPsychotherapy.psychiatricMedications"
              placeholder="Current or past psychiatric medications, response to treatment"
              onBlur={handleBlur}
              value={
                values?.individualPsychotherapy?.psychiatricMedications || ""
              }
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <CustomTextArea
              disabled={viewOnly}
              label="History of Suicidal Thoughts/Self-Harm"
              rows={2}
              variant="outlined"
              name="individualPsychotherapy.suicidalThoughtsHistory"
              placeholder="Past or current suicidal ideation, attempts, self-harm behaviors"
              onBlur={handleBlur}
              value={
                values?.individualPsychotherapy?.suicidalThoughtsHistory || ""
              }
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <CustomTextArea
              disabled={viewOnly}
              label="Substance Use History"
              rows={2}
              variant="outlined"
              name="individualPsychotherapy.substanceUseHistory"
              placeholder="Alcohol, drugs, tobacco use - past or current"
              onBlur={handleBlur}
              value={
                values?.individualPsychotherapy?.substanceUseHistory || ""
              }
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <CustomTextArea
              disabled={viewOnly}
              label="Trauma History"
              rows={2}
              variant="outlined"
              name="individualPsychotherapy.traumaHistory"
              placeholder="History of trauma, abuse, neglect, significant adverse experiences"
              onBlur={handleBlur}
              value={values?.individualPsychotherapy?.traumaHistory || ""}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <CustomTextArea
              disabled={viewOnly}
              label="Current Coping Mechanisms"
              rows={2}
              variant="outlined"
              name="individualPsychotherapy.currentCopingMechanisms"
              placeholder="How do you currently cope with stress? What helps?"
              onBlur={handleBlur}
              value={
                values?.individualPsychotherapy?.currentCopingMechanisms || ""
              }
              onChange={handleChange}
            />
          </Grid>
        </Grid>
      </Box>
      */}

      {/* Primary Concerns & Service Focus Section */}
      <Box sx={{ mt: 1 }}>
        <Text variant="subtitle1" fontWeight="500">
          Primary Concerns & Service Focus
        </Text>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} sm={6} lg={4}>
            <Input
              label="Chief Concern/Reason for Referral"
              textFieldProps={{
                name: "individualPsychotherapy.chiefConcern",
                value: values?.individualPsychotherapy?.chiefConcern || "",
                onChange: handleChange,
                onBlur: handleBlur,
                placeholder: "Selection Text",
                disabled: viewOnly,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={4}>
            <CustomDatePicker
              disabled={viewOnly}
              label="Onset Date/ When first Noticed"
              placeholder="Selection Text"
              value={
                values?.individualPsychotherapy?.onsetDate
                  ? dayjs(values.individualPsychotherapy.onsetDate)
                  : null
              }
              onChange={(value) =>
                setFieldValue("individualPsychotherapy.onsetDate", value)
              }
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={4}>
            <Input
              label="Primary Care Physician"
              textFieldProps={{
                value:
                  values?.individualPsychotherapy
                    ?.primaryCarePhysicianServiceFocused || "",
                onChange: (e) =>
                  setFieldValue(
                    "individualPsychotherapy.primaryCarePhysicianServiceFocused",
                    e.target.value
                  ),
                placeholder: "Selection Text",
                disabled: viewOnly,
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Text variant="body2" fontWeight="500" mb={0.5}>
              Areas of Concern (Select all that apply)
            </Text>
            <Grid container spacing={0}>
              {AREAS_OF_CONCERN.map((concern) => (
                <Grid item xs={12} sm={6} md={3} key={concern.value}>
                  <FormControlLabel
                    disabled={viewOnly}
                    control={
                      <Checkbox
                        disabled={viewOnly}
                        checked={
                          values?.individualPsychotherapy?.areasOfConcern?.[
                            concern.value
                          ] || false
                        }
                        onChange={(e) =>
                          setFieldValue(
                            `individualPsychotherapy.areasOfConcern.${concern.value}`,
                            e.target.checked
                          )
                        }
                        name={`individualPsychotherapy.areasOfConcern.${concern.value}`}
                      />
                    }
                    label={concern.label}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Input
              label="Previous Treatment/Therapy"
              textFieldProps={{
                name: "individualPsychotherapy.previousTreatmentTherapy",
                value:
                  values?.individualPsychotherapy?.previousTreatmentTherapy ||
                  "",
                onChange: handleChange,
                onBlur: handleBlur,
                placeholder: "Selection Text",
                disabled: viewOnly,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Input
              label="Treatment Goals & Expectations"
              textFieldProps={{
                name: "individualPsychotherapy.treatmentGoals",
                value: values?.individualPsychotherapy?.treatmentGoals || "",
                onChange: handleChange,
                onBlur: handleBlur,
                placeholder: "Selection Text",
                disabled: viewOnly,
              }}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Developmental & Family History Section */}
      <Box sx={{ mt: 1 }}>
        <Text variant="subtitle1" fontWeight="500">
          Developmental & Family History
        </Text>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} sm={6} lg={4}>
            <Input
              label="Birth History(For Children/adolscents)"
              textFieldProps={{
                name: "individualPsychotherapy.birthHistory",
                value: values?.individualPsychotherapy?.birthHistory || "",
                onChange: handleChange,
                onBlur: handleBlur,
                placeholder: "Selection Text",
                disabled: viewOnly,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={4}>
            <Input
              label="Developmental Milestones"
              textFieldProps={{
                name: "individualPsychotherapy.developmentalMilestones",
                value:
                  values?.individualPsychotherapy?.developmentalMilestones ||
                  "",
                onChange: handleChange,
                onBlur: handleBlur,
                placeholder: "Selection Text",
                disabled: viewOnly,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={4}>
            <Input
              label="Family Medical/Mental Health History"
              textFieldProps={{
                name: "individualPsychotherapy.familyMedicalMentalHealthHistory",
                value:
                  values?.individualPsychotherapy
                    ?.familyMedicalMentalHealthHistory || "",
                onChange: handleChange,
                onBlur: handleBlur,
                placeholder: "Selection Text",
                disabled: viewOnly,
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Input
              label="Current Support System"
              textFieldProps={{
                name: "individualPsychotherapy.currentSupportSystem",
                value:
                  values?.individualPsychotherapy?.currentSupportSystem || "",
                onChange: handleChange,
                onBlur: handleBlur,
                placeholder: "Selection Text",
                disabled: viewOnly,
              }}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Additional Information Section */}
      <Box sx={{ mt: 1 }}>
        <Text variant="subtitle1" fontWeight="500">
          Additional Information
        </Text>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} sm={6} lg={4}>
            <Input
              label="Education Level/Grade"
              textFieldProps={{
                name: "individualPsychotherapy.educationLevel",
                value: values?.individualPsychotherapy?.educationLevel || "",
                onChange: handleChange,
                onBlur: handleBlur,
                placeholder: "Selection Text",
                disabled: viewOnly,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={4}>
            <Input
              label="Occupation"
              textFieldProps={{
                name: "individualPsychotherapy.occupation",
                value: values?.individualPsychotherapy?.occupation || "",
                onChange: handleChange,
                onBlur: handleBlur,
                placeholder: "Selection Text",
                disabled: viewOnly,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={4}>
            <Input
              label="Employment Status"
              textFieldProps={{
                name: "individualPsychotherapy.employmentStatus",
                value: values?.individualPsychotherapy?.employmentStatus || "",
                onChange: handleChange,
                onBlur: handleBlur,
                placeholder: "Selection Text",
                disabled: viewOnly,
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Input
              label="Referral Source"
              textFieldProps={{
                name: "individualPsychotherapy.referralSource",
                value: values?.individualPsychotherapy?.referralSource || "",
                onChange: handleChange,
                onBlur: handleBlur,
                placeholder: "Selection Text",
                disabled: viewOnly,
              }}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Insurance & Payment Information Section */}
      <Box sx={{ mt: 1 }}>
        <Text variant="subtitle1" fontWeight="500">
          Insurance & Payment Information
        </Text>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} sm={6}>
            <Input
              label="Insurance Provider"
              textFieldProps={{
                name: "individualPsychotherapy.insuranceProvider",
                value: values?.individualPsychotherapy?.insuranceProvider || "",
                onChange: handleChange,
                onBlur: handleBlur,
                placeholder: "Selection Text",
                disabled: viewOnly,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Input
              label="Policy/Member ID Number"
              textFieldProps={{
                name: "individualPsychotherapy.policyMemberId",
                value: values?.individualPsychotherapy?.policyMemberId || "",
                onChange: handleChange,
                onBlur: handleBlur,
                placeholder: "Selection Text",
                disabled: viewOnly,
              }}
            />
          </Grid>
        </Grid>
      </Box>
    </Flex>
  );
};

export default IndividualPsychotherapy;

// Constants
const AREAS_OF_CONCERN = [
  { label: "Depression/Low Mood", value: "depressionLowMood" },
  { label: "Anger Management", value: "angerManagement" },
  { label: "Relationship Issues", value: "relationshipIssues" },
  { label: "Stress Management", value: "stressManagement" },
  { label: "Anxiety/ Worry", value: "anxietyWorry" },
  { label: "Self-esteem Issues", value: "selfEsteemIssues" },
  { label: "Grief/ Loss", value: "griefLoss" },
  { label: "Communication Problems", value: "communicationProblems" },
  { label: "Trauma/ PTSD", value: "traumaPtsd" },
  { label: "Behavioural Concerns", value: "behavioralConcerns" },
  { label: "Substance Use", value: "substanceUse" },
  { label: "Eating/Body Image", value: "eatingBodyImage" },
];
